import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KontaktPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success("Nachricht erfolgreich gesendet!", {
        description: "Wir werden uns schnellstmöglich bei Ihnen melden."
      });
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Fehler beim Senden", {
        description: "Bitte versuchen Sie es später erneut oder rufen Sie uns an."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="kontakt-page" style={{ backgroundColor: '#372d24' }}>
      {/* Hero Section */}
      <section className="pt-12 pb-6" style={{ backgroundColor: '#372d24' }} data-testid="kontakt-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
              Nehmen Sie Kontakt mit uns auf
            </h1>
            <p className="text-lg leading-relaxed" style={{ color: '#dbcebe' }}>
              Haben Sie Fragen oder möchten Sie ein Projekt mit uns besprechen?<br />
              Wir freuen uns auf Ihre Nachricht und melden uns schnellstmöglich bei Ihnen.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-8 pb-16" style={{ backgroundColor: '#372d24' }} data-testid="kontakt-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-[#4a3d32]" style={{ backgroundColor: '#2a2219' }}>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-amber-500/10 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#dbcebe' }}>Telefon</h3>
                    <a 
                      href="tel:+491621608568" 
                      className="text-sm hover:text-amber-400 transition-colors"
                      style={{ color: '#a89f94' }}
                      data-testid="kontakt-phone"
                    >
                      +49 162 1608568
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#4a3d32]" style={{ backgroundColor: '#2a2219' }}>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-amber-500/10 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#dbcebe' }}>E-Mail</h3>
                    <a 
                      href="mailto:info@trockenbau-be.de" 
                      className="text-sm hover:text-amber-400 transition-colors"
                      style={{ color: '#a89f94' }}
                      data-testid="kontakt-email"
                    >
                      info@trockenbau-be.de
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#4a3d32]" style={{ backgroundColor: '#2a2219' }}>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-amber-500/10 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#dbcebe' }}>Erreichbarkeit</h3>
                    <p className="text-sm" style={{ color: '#a89f94' }}>
                      Mo - Fr: 07:00 - 18:00 Uhr<br />
                      Sa: Nach Vereinbarung
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
                    Nachricht senden
                  </h2>

                  {isSuccess ? (
                    <div className="text-center py-8" data-testid="success-message">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500/10 rounded-full mb-4">
                        <CheckCircle2 className="h-6 w-6 text-green-500" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#dbcebe' }}>
                        Vielen Dank für Ihre Nachricht!
                      </h3>
                      <p className="mb-4 text-sm" style={{ color: '#a89f94' }}>
                        Wir werden uns schnellstmöglich bei Ihnen melden.
                      </p>
                      <Button 
                        onClick={() => setIsSuccess(false)}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-white hover:bg-zinc-800"
                        data-testid="send-another-btn"
                      >
                        Weitere Nachricht senden
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label htmlFor="name" className="text-sm" style={{ color: '#dbcebe' }}>Name *</Label>
                          <Input 
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Ihr Name"
                            required
                            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500 h-9"
                            data-testid="input-name"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="email" className="text-sm" style={{ color: '#dbcebe' }}>E-Mail *</Label>
                          <Input 
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="ihre@email.de"
                            required
                            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500 h-9"
                            data-testid="input-email"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label htmlFor="phone" className="text-sm" style={{ color: '#dbcebe' }}>Telefon</Label>
                          <Input 
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+49 123 456789"
                            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500 h-9"
                            data-testid="input-phone"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="subject" className="text-sm" style={{ color: '#dbcebe' }}>Betreff *</Label>
                          <Input 
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="Worum geht es?"
                            required
                            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500 h-9"
                            data-testid="input-subject"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="message" className="text-sm" style={{ color: '#dbcebe' }}>Nachricht *</Label>
                        <Textarea 
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Beschreiben Sie Ihr Anliegen..."
                          rows={4}
                          required
                          className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500"
                          data-testid="input-message"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold h-10"
                        disabled={isSubmitting}
                        data-testid="submit-btn"
                      >
                        {isSubmitting ? (
                          "Wird gesendet..."
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Nachricht senden
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-zinc-500 text-center">
                        Mit dem Absenden stimmen Sie unserer{" "}
                        <a href="/datenschutz" className="text-amber-400 hover:underline">
                          Datenschutzerklärung
                        </a>{" "}
                        zu.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KontaktPage;
